/**
 * §4 — the headline band and the `Case Load` figure strip.
 *
 * §4's panel note is a promise: `Every Figure Computed From The Case Register Below`. Four of
 * the five keep it. The fifth, `Suspensions Active`, counts `penalty.matches > 0` and there
 * is no penalty anywhere in this slice — §13's sanction tables have no writer — so it prints
 * **not built** instead of a figure.
 *
 * That choice is the whole point of the panel. A `0` in that box is a sentence — *nobody at
 * this tournament is currently suspended* — and it would be a sentence this build cannot
 * support. The band's own count line drops the same figure for the same reason and says so,
 * rather than printing `0 matches suspended` in alert red under a strip that knows nothing
 * about suspensions.
 *
 * §4's jurisdiction-and-conflict flow (gap UX-R3 — the TD's own country, the conflict warning
 * at case creation and the `Chaired By` delegation override) is **not built**. The case record
 * carries `tdConflicted`; nothing computes it and no delegation is offered. The note under the
 * strip says so rather than leaving the absence to be inferred.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO LINES THAT USED TO BE FIXED STRINGS, AND ARE NOW READS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The band said `20th Asian Games Aichi-Nagoya 2026` and the panel head said
 * `Bound Instrument · FIH Code Of Conduct, March 2025`. Both were typed here.
 *
 * The second was the worse of the two. §2 keys the whole offence catalogue to a SPECIFIC Code
 * edition because article lettering shifts between editions, and `bound_instrument` is the
 * record of which one this tournament runs — so a register that NAMED an edition it had not
 * read was asserting the one fact this module is built to derive. On a tournament bound to a
 * different edition it named the wrong instrument, on a page whose whole subject is charges
 * lettered under it.
 *
 * Both are props now. `tournament` comes from the session (`GET /auth/me`) and `instrument`
 * from `GET /configuration/code-of-conduct`, which is the catalogue read `ChargePicker`
 * already makes. Each is nullable and each draws NOTHING when it is null — not a default
 * edition, and not the word "loading" on a printed register.
 */

import type { BoundCodeInstrumentView, HearingCaseView } from "../../api/types.js";
import { caseLoadFigures } from "./caseText.js";

export interface CaseLoadPanelProps {
  hearings: readonly HearingCaseView[];
  /**
   * The event, for the band. Null while the session is booting and null when no tournament is
   * set up; the band drops the clause either way rather than naming an event it has not read.
   */
  tournamentName?: string | null;
  /**
   * The Code edition this tournament is bound to. Null while the catalogue read is in flight
   * AND when the tournament has bound none — the two are one sentence here, because in both
   * cases this panel has not been told which instrument is in force.
   */
  instrument?: BoundCodeInstrumentView | null;
  /**
   * Rendered beside the band's own title. The page owns its `How to use` drawer, and this
   * band is the only title the hearings screen draws, so the control is passed in rather than
   * the panel growing a second heading above itself.
   */
  titleAction?: React.ReactNode;
}

export function CaseLoadPanel({
  hearings,
  tournamentName = null,
  instrument = null,
  titleAction = null,
}: CaseLoadPanelProps): React.JSX.Element {
  const figures = caseLoadFigures(hearings);
  const total = figures[0]?.value ?? 0;
  const open = figures[1]?.value ?? 0;
  const decided = figures[3]?.value ?? 0;

  return (
    <>
      <div className="repband">
        <h2>
          Disciplinary Case Register{" "}
          <span className="md" data-testid="caseBandEvent">
            {tournamentName === null ? null : `· ${tournamentName} `}· TD Jurisdiction, Code Of
            Conduct Art 1.7
          </span>
        </h2>
        {titleAction}
        <span style={{ flex: 1 }} />
        <p className="rosterband__count" data-testid="caseBandCount">
          <b>{total}</b> {total === 1 ? "case" : "cases"} <span aria-hidden="true">·</span>{" "}
          <b>{open}</b> open <span aria-hidden="true">·</span> <b>{decided}</b> decided{" "}
          <span aria-hidden="true">·</span> matches suspended not tracked yet
        </p>
      </div>

      <section
        className="panel panel--d1"
        aria-labelledby="caseLoadTitle"
        style={{ marginTop: 18 }}
        data-howto="case-load"
      >
        <div className="panel__head">
          <h3 className="panel__title" id="caseLoadTitle">
            Case Load
          </h3>
          <span className="panel__note">Every Figure Computed From The Case Register Below</span>
          <span className="spacer" />
          {instrument === null ? null : (
            <span className="panel__note" data-testid="bound-instrument">
              Bound Instrument · {instrument.title}, {instrument.versionLabel}
            </span>
          )}
        </div>

        <div className="figstrip" data-testid="caseLoad">
          {figures.map((figure) => (
            <div className="statbox" key={figure.key} data-testid={`fig-${figure.key}`}>
              <div className="statbox__head">
                <span className="statbox__title">{figure.key}</span>
              </div>
              {figure.value === null ? (
                <div className="statbox__fraction">
                  <b>Not Built</b>
                </div>
              ) : (
                <div
                  className="statbox__figure"
                  style={
                    figure.tone === "good"
                      ? { color: "var(--good)" }
                      : figure.tone === "alert"
                        ? { color: "var(--alert)" }
                        : undefined
                  }
                >
                  {figure.value}
                </div>
              )}
              <p className="statbox__note">{figure.sub}</p>
            </div>
          ))}
        </div>

        <div className="panel__foot">
          <b>Two things are not built yet, so nothing above counts them.</b> No penalty,
          suspension or referral is recorded here, so <i>Suspensions Active</i> has nothing to
          count. And nothing yet checks whether a case involves your own country: the case
          record can be marked as a conflict, but the system does not spot one for you and
          there is no way to hand the chair to a deputising ATD.
        </div>
      </section>
    </>
  );
}
